import { browserHistory } from 'react-router'
import { connect, useDispatch, useSelector } from 'react-redux'
import Immutable from 'immutable'
import PropTypes from 'prop-types'
/* global OffAmazonPayments */
import React from 'react'
import cc from 'classcat'
import omit from 'lodash/omit'

import { addLineItemProductList, addPayment, sendButtonClick } from '../../../utils/googleAnalytics'
import { loadCart } from '../../../store/actions'
import { withQueryAndScope } from '../../../urlGenerators'
import addScript from '../../../utils/addScript'
import compose from '../../../utils/compose'
import translate from '../../../utils/translate'
import withI18n from '../../../components/withI18n'

function getAriaLabel(isThemePreview, hasInvalidCoupon, t) {
  const translationPrefix = 'views.storefrontView.basketPageSection.checkoutLinkButton.disabledState.explanations.'
  if (!isThemePreview && !hasInvalidCoupon) return null
  if (isThemePreview) {
    return t(`${translationPrefix}themePreview`)
  }
  return t(`${translationPrefix}couponConditionsNotMet`)
}

// https://github.com/oliviertassinari/babel-plugin-transform-react-remove-prop-types/blob/v0.4.15/README.md#is-it-safe
export const CheckoutButtonRawPropTypes = {
  cart: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  tReady: PropTypes.bool,
  className: PropTypes.string,
  dispatch: PropTypes.func,
  i18n: PropTypes.object,
  isCartBusy: PropTypes.bool,
  isThemePreview: PropTypes.bool.isRequired,
}

export function CheckoutButtonRaw(props) {
  const { className, isCartBusy = false, isThemePreview, cart, t } = props
  const isBeyond = useSelector((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const location = useSelector((state) => state.get('location'))
  const htmlAttributes = omit(props, Object.keys(CheckoutButtonRawPropTypes))
  const hasInvalidCoupon = cart.coupon ? cart.coupon.validationErrors.length > 0 : false
  const isMinimiumOrderPriceReached = cart.checkoutState ? cart.checkoutState.priceValidToOrder !== false : true
  const dispatch = useDispatch()

  const [invalidProducts, setInvalidProducts] = React.useState(
    cart.productLineItems.some((item) => item.productId === null),
  )

  React.useEffect(() => {
    setInvalidProducts(cart.productLineItems.some((item) => item.productId === null))
  }, [cart])

  const gotoCheckout = async () => {
    if (isBeyond) {
      // Stay in React land
      browserHistory.push(withQueryAndScope(cart.checkoutUrl, location))
    } else {
      // The story ends, you wake up in your epages6 checkout

      // load actual cart for checking invalid prd's in parent views/cart.jsx
      const updatedCart = await dispatch(loadCart())

      // setInvalidProducts(cart.productLineItems.some((item) => item.productId === null))
      if (!updatedCart.productLineItems.some((item) => item.productId === null)) {
        window.location.href = cart.checkoutUrl
      }
    }
  }

  return (
    <div>
      <button
        {...htmlAttributes}
        className={cc([
          className,
          {
            'checkout-pending': isCartBusy,
            'ep-tooltip': isThemePreview || hasInvalidCoupon,
          },
        ])}
        type="button"
        aria-label={getAriaLabel(isThemePreview, hasInvalidCoupon, t)}
        disabled={isThemePreview || isCartBusy || hasInvalidCoupon || !isMinimiumOrderPriceReached || invalidProducts}
        onClick={gotoCheckout}
      />
    </div>
  )
}
CheckoutButtonRaw.propTypes = CheckoutButtonRawPropTypes

export function PaypalButtonRaw(props) {
  const { isCartBusy = false, shape = 'pill', color = 'blue', size = '26', cart, className, isThemePreview } = props
  const buttonUrl = `https://www.paypalobjects.com/webstatic/en_US/i/btn/png/${color}-${shape}-paypalcheckout-${size}px.png`
  const paypalFormRef = React.useRef()
  const hasInvalidCoupon = cart.coupon ? cart.coupon.validationErrors.length > 0 : false

  const onSubmit = (event) => {
    event.preventDefault()
    const hitCallback = () => paypalFormRef.current.submit()

    addLineItemProductList(Immutable.fromJS(cart.productLineItems))
    addPayment('PayPal Express', hitCallback)
  }

  const buttonData = cart.checkoutButtons.find((button) => button.name === 'paypalexpress')

  if (!buttonData) return null

  const classNames = cc([
    className,
    {
      'checkout-pending': isCartBusy,
      'is-hidden': isThemePreview || hasInvalidCoupon,
    },
  ])

  return (
    <form method="post" action={cart.checkoutUrl} className={classNames} ref={paypalFormRef} onSubmit={onSubmit}>
      <input type="hidden" name="PaymentMethodID" value={buttonData.paymentMethodId} />
      <input type="hidden" name="ChangeAction" value="PickupBasket" />
      <input type="hidden" name="PickupToken" value={cart.pickupToken} />
      <input type="hidden" name="ChangeAction" value="BlockMonitor" />
      <input type="hidden" name="ChangeAction" value="PayPalExpress" />
      <button
        type="submit"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
        onClick={() => sendButtonClick('payment', 'PayPal Express')}
      >
        <img src={buttonUrl} alt="PayPal Checkout" />
      </button>
    </form>
  )
}
PaypalButtonRaw.propTypes = {
  cart: PropTypes.object.isRequired,
  isThemePreview: PropTypes.bool.isRequired,
  className: PropTypes.string,
  color: PropTypes.string,
  dispatch: PropTypes.func,
  isCartBusy: PropTypes.bool,
  shape: PropTypes.string,
  size: PropTypes.string,
}

export function AmazonButtonRaw({ className, cart, isThemePreview, isCartBusy = false }) {
  const amazonFormRef = React.useRef()
  const hasInvalidCoupon = cart.coupon ? cart.coupon.validationErrors.length > 0 : false
  const buttonData = cart.checkoutButtons.find((button) => button.name === 'amazonpayments')

  React.useEffect(() => {
    const buttonData = cart.checkoutButtons.find((button) => button.name === 'amazonpayments')

    // do not do anything if we do not have an amazon button
    if (buttonData) {
      // if the amazon script was not loaded yet create the button when it is ready and add the script tag to the head
      if (!window.OffAmazonPayments) {
        window.onAmazonPaymentsReady = () => createAmazonPaymentsButton(buttonData)
        addScript(buttonData.widgetUrl)
      } else {
        // if the amazon script is already loaded just create the button
        createAmazonPaymentsButton(buttonData)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!buttonData) return null

  /**
   * Triggers the checkout with Amazon Pay
   *
   * @param {Object} orderReference An Amazon object of the order
   */
  const checkoutWithAmazon = (orderReference) => {
    amazonFormRef.current.AmazonRefId.value = orderReference.getAmazonOrderReferenceId()
    amazonFormRef.current.submit()
  }

  /**
   * Creates the Amazon Pay button
   *
   * @param   {Object}    buttonData  Data for the Amazon Pay button
   * @returns {Button}                The Amazon Pay button
   */
  const createAmazonPaymentsButton = (buttonData) => {
    return new OffAmazonPayments.Widgets.Button({
      sellerId: buttonData.dataMerchantId,
      onSignIn: (orderReference) => checkoutWithAmazon(orderReference),
    }).bind('PayWithAmazon')
  }

  const classNames = cc([
    className,
    {
      'checkout-pending': isCartBusy,
      'is-hidden': isThemePreview || hasInvalidCoupon,
    },
  ])

  return (
    <div className={classNames}>
      <div id="PayWithAmazon" onClick={() => sendButtonClick('payment', 'Amazon Pay')}>
        <img src={buttonData.image} alt="Amazon Pay Checkout" style={{ cursor: 'pointer' }} />
      </div>
      <form method="post" action={cart.checkoutUrl} ref={amazonFormRef}>
        <input type="hidden" name="AmazonRefId" value="" />
        <input type="hidden" name="ViewAction" value="ViewMultiCheckoutAmazonAddress" />
        <input type="hidden" name="UpdateViewAction" value="ViewMultiCheckoutAmazonAddress" />
        <input type="hidden" name="ChangeAction" value="PayWithAmazonPayments" />
      </form>
    </div>
  )
}
AmazonButtonRaw.propTypes = {
  cart: PropTypes.object.isRequired,
  className: PropTypes.string,
  dispatch: PropTypes.func,
  isCartBusy: PropTypes.bool,
  isThemePreview: PropTypes.bool.isRequired,
}

const mapStateToProps = (state) => ({
  cart: state.get('cart').toJS(),
  isCartBusy: state.getIn(['view', 'cart', 'isBusy']),
  // Check if a preview theme is set.
  // We have to check for an attribute of `themePreview`, because
  // `themePreview` itself is always found on the server.
  isThemePreview: state.hasIn(['view', 'themePreview', 'theme']),
})

export const CheckoutButton = compose(
  withI18n('shop'),
  translate(),
  connect(mapStateToProps, null, null, { pure: false }),
)(CheckoutButtonRaw)

export const PaypalButton = connect(mapStateToProps, null, null, { pure: false })(PaypalButtonRaw)
export const AmazonButton = connect(mapStateToProps, null, null, { pure: false })(AmazonButtonRaw)
